import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';

const Pricing = () => {
  const plans = [
    {
      name: 'MT5 Indicator',
      price: '$49',
      period: '/month',
      features: [
        'Premium MT5 Indicator',
        'Real-time Volume Analysis',
        'Custom Alert System',
        'Lifetime Updates',
        'MT5 Desktop Support',
        'Email Support'
      ],
      popular: false
    },
    {
      name: 'MT5 Pro Bundle',
      price: '$99',
      period: '/month',
      features: [
        'MT5 Indicator + Algorithm',
        'Everything in single plans',
        'High-Speed Execution',
        'Advanced Backtesting',
        'Priority Support',
        'Multiple MT5 Accounts',
        'Private Community Access'
      ],
      popular: true
    },
    {
      name: 'MT5 Algorithm',
      price: '$69',
      period: '/month',
      features: [
        'Premium MT5 Algorithm',
        'Automated Execution',
        'Risk Management Tools',
        'Backtesting Engine',
        'Lifetime Updates',
        'Email Support'
      ],
      popular: false
    }
  ];

  const comparisonFeatures = [
    { feature: 'MT5 Indicator', indicator: true, bundle: true, algorithm: false },
    { feature: 'MT5 Algorithm', indicator: false, bundle: true, algorithm: true },
    { feature: 'Real-time Analysis', indicator: true, bundle: true, algorithm: true },
    { feature: 'Automated Execution', indicator: false, bundle: true, algorithm: true },
    { feature: 'Backtesting Engine', indicator: 'Basic', bundle: 'Advanced', algorithm: 'Standard' },
    { feature: 'Risk Management', indicator: false, bundle: true, algorithm: true },
    { feature: 'Account Connections', indicator: '1 Account', bundle: '5 Accounts', algorithm: '1 Account' },
    { feature: 'Support Level', indicator: 'Email', bundle: 'Priority 24/7', algorithm: 'Email' }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-[#0B0F1A] via-[#1a1147] to-[#0B0F1A] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#7C3AED]/10 via-transparent to-[#00D4FF]/10" />
        
        <div className="relative z-10 max-w-[1280px] mx-auto px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-6xl font-bold mb-6">MT5 Professional Pricing</h1>
            <p className="text-xl text-[#9CA3AF] max-w-3xl mx-auto">
              Choose the right professional tools for your MT5 trading workspace.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-24 bg-[#0B0F1A]">
        <div className="max-w-[1280px] mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <motion.div
                  className={`relative rounded-2xl p-8 h-full flex flex-col ${
                    plan.popular
                      ? 'bg-gradient-to-br from-[#7C3AED]/20 to-[#00D4FF]/20 border-2 border-[#7C3AED]'
                      : 'bg-white/5 backdrop-blur-lg border border-white/10'
                  }`}
                  whileHover={{ scale: 1.02, translateY: -8 }}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#7C3AED] to-[#00D4FF] text-white px-6 py-1 rounded-full text-sm font-semibold whitespace-nowrap">
                      Best Value Bundle
                    </div>
                  )}
                  
                  <h3 className="text-3xl font-bold mb-2">{plan.name}</h3>
                  <div className="flex items-baseline mb-6">
                    <span className="text-5xl font-bold">{plan.price}</span>
                    <span className="text-[#9CA3AF] ml-2">{plan.period}</span>
                  </div>
                  
                  <ul className="space-y-4 mb-8 flex-grow">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <Check className="text-[#00D4FF] flex-shrink-0" size={20} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link to="/signup" className="w-full">
                    <motion.button
                      className={`w-full py-4 rounded-lg font-semibold mt-auto ${
                        plan.popular
                          ? 'bg-[#7C3AED] text-white hover:shadow-2xl hover:shadow-[#7C3AED]/50'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      } transition-all`}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Get Started Now
                    </motion.button>
                  </Link>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-[1280px] mx-auto px-8">
          <AnimatedSection>
            <h2 className="text-4xl font-bold text-center mb-12">Compare Products</h2>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-6 text-lg font-semibold">Feature</th>
                    <th className="text-center p-6 text-lg font-semibold">Indicator</th>
                    <th className="text-center p-6 text-lg font-semibold">Algorithm</th>
                    <th className="text-center p-6 text-lg font-semibold text-[#00D4FF]">Pro Bundle</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((item, index) => (
                    <tr key={index} className="border-b border-white/10 last:border-0">
                      <td className="p-6 font-medium">{item.feature}</td>
                      <td className="p-6 text-center">
                        {typeof item.indicator === 'boolean' ? (
                          item.indicator ? <Check className="mx-auto text-[#00D4FF]" size={20} /> : <span className="text-[#9CA3AF]">-</span>
                        ) : (
                          <span className="text-sm">{item.indicator}</span>
                        )}
                      </td>
                      <td className="p-6 text-center">
                        {typeof item.algorithm === 'boolean' ? (
                          item.algorithm ? <Check className="mx-auto text-[#00D4FF]" size={20} /> : <span className="text-[#9CA3AF]">-</span>
                        ) : (
                          <span className="text-sm">{item.algorithm}</span>
                        )}
                      </td>
                      <td className="p-6 text-center bg-[#7C3AED]/5">
                        {typeof item.bundle === 'boolean' ? (
                          item.bundle ? <Check className="mx-auto text-[#00D4FF]" size={20} /> : <span className="text-[#9CA3AF]">-</span>
                        ) : (
                          <span className="text-sm font-semibold">{item.bundle}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-[#7C3AED]/20 via-[#ec4899]/10 to-[#00D4FF]/20">
        <div className="max-w-[1280px] mx-auto px-8 text-center">
          <AnimatedSection>
            <h2 className="text-4xl font-bold mb-6">Ready to elevate your trading?</h2>
            <p className="text-xl text-[#9CA3AF] mb-8 max-w-2xl mx-auto">
              Start your 14-day free trial today. No credit card required.
            </p>
            <Link to="/signup">
              <motion.button
                className="bg-white text-[#0B0F1A] px-10 py-4 rounded-lg font-bold text-lg hover:shadow-2xl hover:shadow-white/30 transition-all"
                whileHover={{ scale: 1.05, translateY: -4 }}
                whileTap={{ scale: 0.98 }}
              >
                Start Free Trial <ArrowRight className="inline ml-2" size={20} />
              </motion.button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
